import { useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Skeleton, Tooltip } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { getTenantDetail } from '../api/tenant';
import { Topbar } from '../layouts/Topbar';
import { TenantStatusBadge } from '../components/StatusBadge';
import { EventTimeline, EVENT_LABEL } from '../components/Timeline';
import { DangerZone } from '../components/DangerZone';
import { formatDate, formatDateTime } from '../utils/datetime';
import { useAuthStore } from '../store/auth';

type TabKey = 'overview' | 'contract' | 'events' | 'danger';

function getInitialTab(): TabKey {
  const hash = typeof window !== 'undefined' ? window.location.hash.replace('#', '') : '';
  const valid: TabKey[] = ['overview', 'contract', 'events', 'danger'];
  return (valid as string[]).includes(hash) ? (hash as TabKey) : 'overview';
}

function daysLeft(end: string | undefined | null): number | null {
  if (!end) return null;
  const ms = Date.parse(end) - Date.now();
  return Math.floor(ms / 86400000);
}

function daysLeftColor(d: number | null): string {
  if (d == null) return 'var(--c-fg)';
  if (d < 0) return 'var(--c-destructive)';
  if (d <= 7) return 'var(--c-warning)';
  return 'var(--c-success)';
}

const EVENT_TYPES = ['create', 'provision', 'migrate', 'grace', 'read_only', 'suspend', 'resume', 'manually_lock', 'unlock', 'purge', 'warn'] as const;
const EVENTS_PAGE_SIZE = 10;

export function TenantDetailPage() {
  const { id } = useParams<{ id: string }>();
  const tenantId = Number(id);
  const [tab, setTab] = useState<TabKey>(getInitialTab);
  const isAdmin = useAuthStore((s) => s.isAdmin());

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['tenant', tenantId],
    queryFn: () => getTenantDetail(tenantId),
    enabled: !Number.isNaN(tenantId),
  });

  function switchTab(key: TabKey) {
    setTab(key);
    history.replaceState(null, '', `#${key}`);
  }

  if (isLoading || !data) {
    return (
      <>
        <Topbar crumbs={[{ label: '运营' }, { label: '租户管理', href: '/tenants' }, { label: '…' }]} />
        <div className="page"><Skeleton active /></div>
      </>
    );
  }

  const { tenant, currentContract, historyContracts, recentEvents } = data;
  const dLeft = daysLeft(currentContract?.endAt);

  return (
    <>
      <Topbar crumbs={[{ label: '运营' }, { label: '租户管理', href: '/tenants' }, { label: tenant.display_name }]} />
      <div className="page">
        {/* 页面头部 */}
        <div className="page-header">
          <div className="page-header__title">
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6 }}>
              <h1 style={{ margin: 0 }}>{tenant.display_name}</h1>
              <TenantStatusBadge status={tenant.status} />
              {currentContract?.planCode && <span className="tag">{currentContract.planCode}</span>}
            </div>
            <div className="page-header__sub">
              <a
                className="text-mono"
                href={`https://${tenant.code}.owlsome.kzdatong.com`}
                target="_blank"
                rel="noopener noreferrer"
              >{tenant.code}.owlsome.kzdatong.com</a> ·{' '}
              <span className="text-mono">{tenant.db_name}</span> ·{' '}
              创建于 {formatDate(tenant.created_at)}
              {currentContract?.contractNo && (
                <>
                  {' '}· 当前合同 <strong>{currentContract.contractNo}</strong>
                  {' '}至 {formatDate(currentContract.endAt)}
                </>
              )}
            </div>
          </div>
          <div className="page-header__actions">
            {tenant.status === 'terminated' ? (
              <Tooltip title="租户已清退，无法续签">
                <button type="button" className="btn btn-primary" disabled>
                  <ReloadOutlined /> <span>续签合同</span>
                </button>
              </Tooltip>
            ) : (
              <Link to={`/tenants/${tenantId}/renew`} className="btn btn-primary">
                <ReloadOutlined /> <span>续签合同</span>
              </Link>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="tab-bar">
          <span className={`tab${tab === 'overview' ? ' is-active' : ''}`} onClick={() => switchTab('overview')}>概览</span>
          <span className={`tab${tab === 'contract' ? ' is-active' : ''}`} onClick={() => switchTab('contract')}>
            合同 ({historyContracts.length + (currentContract ? 1 : 0)})
          </span>
          <span className={`tab${tab === 'events' ? ' is-active' : ''}`} onClick={() => switchTab('events')}>
            生命周期事件 ({recentEvents.length})
          </span>
          {isAdmin && (
            <span
              className={`tab${tab === 'danger' ? ' is-active' : ''}`}
              onClick={() => switchTab('danger')}
              style={{ color: 'var(--c-destructive)' }}
            >危险操作</span>
          )}
        </div>

        {tab === 'overview' && (
          <OverviewPanel
            tenant={tenant}
            currentContract={currentContract}
            recentEvents={recentEvents}
            dLeft={dLeft}
            onJumpTab={switchTab}
            isAdmin={isAdmin}
          />
        )}
        {tab === 'contract' && (
          <ContractPanel currentContract={currentContract} historyContracts={historyContracts} dLeft={dLeft} />
        )}
        {tab === 'events' && (
          <EventsPanel events={recentEvents} />
        )}
        {tab === 'danger' && isAdmin && (
          <DangerZone tenant={tenant} onRefresh={() => refetch()} />
        )}
      </div>
    </>
  );
}

// ────────── Overview ──────────
function OverviewPanel({
  tenant, currentContract, recentEvents, dLeft, onJumpTab, isAdmin,
}: {
  tenant: Awaited<ReturnType<typeof getTenantDetail>>['tenant'];
  currentContract: Awaited<ReturnType<typeof getTenantDetail>>['currentContract'];
  recentEvents: Awaited<ReturnType<typeof getTenantDetail>>['recentEvents'];
  dLeft: number | null;
  onJumpTab: (k: TabKey) => void;
  isAdmin: boolean;
}) {
  return (
    <div className="grid-overview">
      <div className="flex flex-col gap-6" style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
        {/* 基本信息 */}
        <div className="card">
          <div className="card__header">
            <div className="card__title">基本信息</div>
            <Tooltip title="基本信息编辑接口暂未提供">
              <button type="button" className="btn btn-ghost btn-sm" disabled>编辑</button>
            </Tooltip>
          </div>
          <div className="card__body">
            <div className="meta-grid">
              <div className="meta-grid__item"><label>租户 code</label><div className="val text-mono">{tenant.code}</div></div>
              <div className="meta-grid__item"><label>显示名</label><div className="val">{tenant.display_name}</div></div>
              <div className="meta-grid__item"><label>子域名</label><div className="val text-mono"><a href={`https://${tenant.code}.owlsome.kzdatong.com`} target="_blank" rel="noopener noreferrer">{tenant.code}.owlsome.kzdatong.com</a></div></div>
              <div className="meta-grid__item"><label>数据库</label><div className="val text-mono">{tenant.db_name}</div></div>
              <div className="meta-grid__item"><label>套餐</label><div className="val">{currentContract?.planCode ? <span className="tag">{currentContract.planCode}</span> : '—'}</div></div>
              <div className="meta-grid__item"><label>创建时间</label><div className="val text-mono" title={tenant.created_at}>{formatDateTime(tenant.created_at)}</div></div>
              <div className="meta-grid__item"><label>更新时间</label><div className="val text-mono" title={tenant.updated_at}>{formatDateTime(tenant.updated_at)}</div></div>
            </div>
          </div>
        </div>

        {/* 当前合同摘要 */}
        {currentContract && (
          <div className="card">
            <div className="card__header">
              <div className="card__title">当前合同摘要 · {currentContract.contractNo ?? '—'}</div>
              <span className="text-xs">
                <a href="#contract" onClick={(e) => { e.preventDefault(); onJumpTab('contract'); }}>查看完整合同与历史 →</a>
              </span>
            </div>
            <div className="card__body">
              <div className="meta-grid-3">
                <div className="meta-grid__item"><label>到期</label><div className="val text-mono">{formatDate(currentContract.endAt)}</div></div>
                <div className="meta-grid__item">
                  <label>距到期</label>
                  <div className="val text-strong" style={{ color: daysLeftColor(dLeft) }}>
                    {dLeft == null ? '—' : dLeft < 0 ? `已逾期 ${-dLeft} 天` : `${dLeft} 天`}
                  </div>
                </div>
                <div className="meta-grid__item">
                  <label>窗口期</label>
                  <div className="val">{currentContract.graceDays} / {currentContract.suspendDays} / {currentContract.purgeDays} 天</div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
        {/* 最近事件 */}
        <div className="card">
          <div className="card__header">
            <div className="card__title">最近事件</div>
            <a href="#events" className="text-xs" onClick={(e) => { e.preventDefault(); onJumpTab('events'); }}>查看全部 →</a>
          </div>
          <div className="card__body" style={{ paddingTop: 18 }}>
            {recentEvents.length === 0 ? (
              <div style={{ color: 'var(--c-fg-3)', textAlign: 'center', padding: 24 }}>暂无事件</div>
            ) : (
              <EventTimeline events={recentEvents.slice(0, 5)} />
            )}
          </div>
        </div>

        {/* 危险操作隔离提示（仅 admin 可见） */}
        {isAdmin && (
          <div className="alert alert--warning">
            <div>
              <div className="text-strong mb-2">⚠ 危险操作已隔离</div>
              <div className="text-xs">
                重置 admin / 冻结 / 备份 / 调度清退 等操作已迁移至独立 tab，需输入租户 code 验证后方可触发。
              </div>
              <div className="mt-4" style={{ marginTop: 16 }}>
                <a className="btn btn-secondary btn-sm" href="#danger" onClick={(e) => { e.preventDefault(); onJumpTab('danger'); }}>
                  前往危险操作 →
                </a>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ────────── Contract ──────────
function ContractPanel({
  currentContract, historyContracts, dLeft,
}: {
  currentContract: Awaited<ReturnType<typeof getTenantDetail>>['currentContract'];
  historyContracts: Awaited<ReturnType<typeof getTenantDetail>>['historyContracts'];
  dLeft: number | null;
}) {
  const { id } = useParams<{ id: string }>();
  return (
    <div>
      {currentContract ? (
        <div className="card" style={{ marginBottom: 24 }}>
          <div className="card__header">
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div className="card__title">当前合同 · {currentContract.contractNo ?? '—'}</div>
              <span className="badge badge--active">CURRENT</span>
            </div>
            <Link to={`/tenants/${id}/renew`} className="btn btn-secondary btn-sm">续签合同</Link>
          </div>
          <div className="card__body">
            <div className="meta-grid-3">
              <div className="meta-grid__item"><label>合同号</label><div className="val text-mono">{currentContract.contractNo ?? '—'}</div></div>
              <div className="meta-grid__item"><label>套餐</label><div className="val"><span className="tag">{currentContract.planCode}</span></div></div>
              <div className="meta-grid__item">
                <label>距到期</label>
                <div className="val text-strong" style={{ color: daysLeftColor(dLeft) }}>
                  {dLeft == null ? '—' : dLeft < 0 ? `已逾期 ${-dLeft} 天` : `${dLeft} 天`}
                </div>
              </div>
              <div className="meta-grid__item"><label>起始</label><div className="val text-mono">{formatDate(currentContract.startAt)}</div></div>
              <div className="meta-grid__item"><label>到期</label><div className="val text-mono">{formatDate(currentContract.endAt)}</div></div>
              <div className="meta-grid__item"><label>创建时间</label><div className="val text-mono" title={currentContract.createdAt}>{formatDateTime(currentContract.createdAt)}</div></div>
              <div className="meta-grid__item"><label>宽限期</label><div className="val">{currentContract.graceDays} 天</div></div>
              <div className="meta-grid__item"><label>停服延迟</label><div className="val">{currentContract.suspendDays} 天</div></div>
              <div className="meta-grid__item"><label>清退延迟</label><div className="val">{currentContract.purgeDays} 天</div></div>
            </div>
            {currentContract.notes && (
              <>
                <div className="divider" />
                <div className="meta-grid__item">
                  <label>备注</label>
                  <div className="val" style={{ fontWeight: 400 }}>{currentContract.notes}</div>
                </div>
              </>
            )}
          </div>
        </div>
      ) : (
        <div className="card" style={{ marginBottom: 24 }}>
          <div className="card__body" style={{ color: 'var(--c-fg-3)', textAlign: 'center', padding: 40 }}>暂无当前合同</div>
        </div>
      )}

      <div className="card">
        <div className="card__header">
          <div className="card__title">历史合同（{historyContracts.length} 条）</div>
          <span className="text-xs text-muted">合同永久保留，可追溯</span>
        </div>
        <div className="table-wrap" style={{ border: 0, borderRadius: 0, borderTop: '1px solid var(--c-border)' }}>
          <table className="table">
            <thead>
              <tr>
                <th>合同号</th><th>套餐</th><th>起始</th><th>到期</th>
                <th>窗口期</th><th>备注</th><th>创建时间</th><th>状态</th>
              </tr>
            </thead>
            <tbody>
              {historyContracts.length === 0 && (
                <tr><td colSpan={8} style={{ textAlign: 'center', color: 'var(--c-fg-3)', padding: 24 }}>暂无历史合同</td></tr>
              )}
              {historyContracts.map((c) => (
                <tr key={c.id}>
                  <td className="text-mono">{c.contractNo ?? '—'}</td>
                  <td><span className="tag">{c.planCode}</span></td>
                  <td className="text-mono num">{formatDate(c.startAt)}</td>
                  <td className="text-mono num">{formatDate(c.endAt)}</td>
                  <td className="num text-xs">{c.graceDays} / {c.suspendDays} / {c.purgeDays}</td>
                  <td className="text-xs text-muted" style={{ maxWidth: 240 }}>{c.notes ?? '—'}</td>
                  <td className="text-mono num text-xs" title={c.createdAt}>{formatDateTime(c.createdAt)}</td>
                  <td><span className="badge badge--terminated">expired</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ────────── Events ──────────
function EventsPanel({
  events,
}: {
  events: Awaited<ReturnType<typeof getTenantDetail>>['recentEvents'];
}) {
  const [type, setType] = useState<string>('');
  const [operator, setOperator] = useState<string>('');
  const [from, setFrom] = useState<string>('');
  const [to, setTo] = useState<string>('');
  const [page, setPage] = useState(1);

  const operatorOptions = useMemo(() => {
    const set = new Set<string>();
    for (const e of events) set.add(e.operator_username ?? 'cron');
    return Array.from(set);
  }, [events]);

  const filtered = useMemo(() => {
    return events.filter((e) => {
      if (type && e.event_type !== type) return false;
      const actor = e.operator_username ?? 'cron';
      if (operator && actor !== operator) return false;
      const date = e.occurred_at?.slice(0, 10);
      if (from && date < from) return false;
      if (to && date > to) return false;
      return true;
    });
  }, [events, type, operator, from, to]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / EVENTS_PAGE_SIZE));
  const pageSafe = Math.min(page, totalPages);
  const pageItems = filtered.slice((pageSafe - 1) * EVENTS_PAGE_SIZE, pageSafe * EVENTS_PAGE_SIZE);

  return (
    <div className="card">
      <div className="card__body">
        <div className="events-toolbar">
          <select className="select" value={type} onChange={(e) => { setType(e.target.value); setPage(1); }}>
            <option value="">全部事件类型</option>
            {EVENT_TYPES.map((t) => <option key={t} value={t}>{EVENT_LABEL[t] ?? t}</option>)}
          </select>
          <select className="select" value={operator} onChange={(e) => { setOperator(e.target.value); setPage(1); }}>
            <option value="">全部操作人</option>
            {operatorOptions.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
          <input className="input" type="date" value={from} onChange={(e) => { setFrom(e.target.value); setPage(1); }} style={{ width: 'auto' }} />
          <span className="text-muted">→</span>
          <input className="input" type="date" value={to} onChange={(e) => { setTo(e.target.value); setPage(1); }} style={{ width: 'auto' }} />
          <div style={{ marginLeft: 'auto' }} className="text-xs text-muted">
            共 <strong>{filtered.length}</strong> 条 · append-only
            <Tooltip title="lifecycle_events 表 append-only，不允许 UPDATE/DELETE">
              <span className="help-tip">i</span>
            </Tooltip>
          </div>
        </div>

        {pageItems.length === 0 ? (
          <div style={{ color: 'var(--c-fg-3)', textAlign: 'center', padding: 24 }}>暂无事件</div>
        ) : (
          <EventTimeline events={pageItems} showId />
        )}

        <div className="pagination" style={{ borderTop: 0, padding: '16px 0 0' }}>
          <button type="button" className="btn btn-ghost btn-sm" disabled={pageSafe <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}>‹</button>
          <span className="page-num is-active">{pageSafe}</span>
          <button type="button" className="btn btn-ghost btn-sm" disabled={pageSafe >= totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>›</button>
        </div>
      </div>
    </div>
  );
}
